import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { DateTime } from 'luxon';
import { useLocalStorageState } from '@clover-util/use-storage-state';

// hooks
import { useWindowSize } from 'hooks/breakpoints';

// styles
import { Banner, BannerVariant } from 'components/Banner';
import styles from 'components/CookieBanner.module.scss';

// consts
import { COLO_COOKIE_BANNER_SEEN, COOKIE_BANNER_TEST_ID } from 'components/CookieBanner.const';

type CookieBannerProps = {
  onScrollHeightChanged: (height: number) => void;
};

export const CookieBanner: FC<CookieBannerProps> = ({ onScrollHeightChanged }) => {
  const bannerRef = useRef<HTMLDivElement>(null);
  const bufferHeight = 26;

  const { t } = useTranslation();
  const [fontloaded, setfontsloaded] = useState(false);
  const { width } = useWindowSize();

  useEffect(() => {
    document.fonts.ready.then(function () {
      setfontsloaded(true);
    });
  }, []);

  const [bannerSeenOn, setBannerSeenOn] = useLocalStorageState<string>(COLO_COOKIE_BANNER_SEEN);
  useEffect(() => {
      onScrollHeightChanged(bannerRef.current ? bannerRef?.current?.scrollHeight + bufferHeight : 0);
  }, [onScrollHeightChanged, bannerRef, bannerSeenOn, width, fontloaded]);

  const handleBannerDismiss = useCallback(() => {
    setBannerSeenOn(DateTime.now().toFormat('dd-MMM-yyyy h:m a'));
  }, [setBannerSeenOn]);

  if (bannerSeenOn || !fontloaded) return null;

  return (
    <Banner variant={BannerVariant.ALERT} className={styles.cookieBannerContainer} data-testid={COOKIE_BANNER_TEST_ID.root} >
      <div className={styles.cookieBanner} ref={bannerRef}>
        <div>
          <Trans i18nKey="cookieBanner.disclaimer">
            Our site uses cookies to improve your user experience. By browsing this site you consent to our use of cookies as described in our <a href={t('footerLinks.privacy')} data-testid={COOKIE_BANNER_TEST_ID.link}>Privacy Policy</a>.
          </Trans>
        </div>
        <button onClick={handleBannerDismiss} data-testid={COOKIE_BANNER_TEST_ID.button}>{t('cookieBanner.dismiss')}</button>
      </div>
    </Banner>
  );
};
